import { SaleDialog } from "@/components/interface/sale/SaleDialog";
import { Card } from "@/components/ui/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Search, ShoppingCart, HelpCircle, Info } from "lucide-react";
import { CartDialog } from "@/components/stock/CartDialog";
import { toast } from "sonner";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useNewCart } from "@/hooks/useNewCart";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";

export default function Sell() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isSaleDialogOpen, setIsSaleDialogOpen] = useState(false);
  const [isCartDialogOpen, setIsCartDialogOpen] = useState(false);
  const [quantities, setQuantities] = useState<{
    [key: string]: number;
  }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    cartItems
  } = useNewCart();
  const {
    data: currency
  } = useOwnerCurrency();
  const {
    data: stockItems,
    refetch,
    isLoading,
    error
  } = useQuery({
    queryKey: ["stock_items"],
    queryFn: async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Vous devez être connecté pour accéder à cette page");
      const {
        data: stocks
      } = await supabase.from('business_stocks').select('id').eq('owner_id', user.id);
      if (!stocks?.length) return [];
      const allStockItems = await Promise.all(stocks.map(async stock => {
        const {
          data
        } = await supabase.from('stock_items').select('*').eq('stock_id', stock.id);
        return data || [];
      }));
      return allStockItems.flat().map(item => {
        try {
          const parsedData = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
          return {
            ...item,
            data: parsedData
          };
        } catch (e) {
          console.error("Erreur lors du parsing des données:", e);
          return item;
        }
      });
    },
    staleTime: 1000
  });
  useEffect(() => {
    const channel = supabase.channel('stock_items_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'stock_items'
    }, () => {
      queryClient.invalidateQueries({
        queryKey: ["stock_items"]
      });
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
  const handleQuantityChange = (itemId: string, quantity: number) => {
    if (quantity >= 0) {
      setQuantities({
        ...quantities,
        [itemId]: quantity
      });
    }
  };
  const handleQuickSale = async (item: any, quantity: number = 1) => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }
      if (quantity > item.quantity) {
        toast.error("Quantité insuffisante en stock");
        return;
      }
      const {
        error: transactionError
      } = await supabase.from('poultry_transactions').insert({
        stock_item_id: item.id,
        transaction_type: 'sale',
        quantity: quantity,
        unit_price: item.data.price,
        total_amount: quantity * item.data.price,
        transaction_date: new Date().toISOString(),
        created_by: user.id,
        payment_method: 'espèces',
        is_fully_paid: true,
        validation_status: 'pending',
        digital_signature: 'Vente rapide'
      });
      if (transactionError) throw transactionError;
      const {
        error: stockError
      } = await supabase.from('stock_items').update({
        quantity: item.quantity - quantity
      }).eq('id', item.id);
      if (stockError) throw stockError;
      
      // Remplacer le toast simple par celui avec plus d'informations
      toast.success(
        "✅ Vente effectuée avec succès !",
        {
          duration: 30000, // 30 secondes d'affichage
          dismissible: true, // Permet à l'utilisateur de fermer manuellement
          important: true, // Donne une priorité plus élevée à ce toast
          className: "p-4 bg-white border border-gray-200 shadow-lg",
          description: "⚠️ Important : Cette vente n'est pas encore comptée comme chiffre d'affaires. Allez dans l'onglet 'Historique' pour valider cette vente.",
          action: {
            label: "Aller à l'Historique",
            onClick: () => navigate("/owner/history")
          }
        }
      );
      
      setQuantities({
        ...quantities,
        [item.id]: 0
      });
      refetch();
    } catch (error) {
      console.error("Erreur lors de la vente rapide:", error);
      toast.error("Erreur lors de la vente rapide");
    }
  };
  const handleAddToCart = async (item: any) => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }
      const quantity = quantities[item.id] || 0;
      if (quantity <= 0) {
        toast.error("Veuillez saisir une quantité");
        return;
      }
      if (quantity > item.quantity) {
        toast.error("Quantité insuffisante en stock");
        return;
      }
      const {
        error
      } = await supabase.from('cart_items').insert({
        stock_item_id: item.id,
        quantity: quantity,
        created_by: user.id
      });
      if (error) throw error;
      toast.success("Produit ajouté au panier");
      setQuantities({
        ...quantities,
        [item.id]: 0
      });
      queryClient.invalidateQueries({
        queryKey: ["stock_items"]
      });
      queryClient.invalidateQueries({
        queryKey: ["cart_items"]
      });
    } catch (error) {
      console.error("Erreur lors de l'ajout au panier:", error);
      toast.error("Erreur lors de l'ajout au panier");
    }
  };
  const filteredItems = stockItems?.filter(item => {
    if (!item.data) return false;
    const itemName = String(item.data.name || item.data.Race || '').toLowerCase();
    const itemDescription = String(item.data.description || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return itemName.includes(query) || itemDescription.includes(query);
  });
  const totalPages = Math.ceil((filteredItems?.length || 0) / itemsPerPage);
  const currentItems = filteredItems?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  if (error) {
    return <div className="container py-6">
        <div className="text-center text-red-500">
          {error instanceof Error ? error.message : "Une erreur s'est produite"}
        </div>
      </div>;
  }
  if (isLoading) {
    return <div className="container py-6">
        <div className="text-center">Chargement...</div>
      </div>;
  }
  return <DashboardLayout userType="owner">
      <div className="container py-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Vendre des produits</h1>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 w-full">
                <HelpCircle className="h-5 w-5" />
                <span className="font-normal text-base text-amber-600">Comment utiliser les modes de vente ?</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] p-0">
              <DialogHeader className="p-6 pb-0">
                <DialogTitle>Guide des Modes de Vente</DialogTitle>
              </DialogHeader>
              <ScrollArea className="px-6 pb-6" style={{
              maxHeight: "calc(80vh - 60px)"
            }}>
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Mode "Vente Rapide"</h3>
                    <div className="pl-4 space-y-2">
                      <p className="font-medium">Quand l'utiliser :</p>
                      <ul className="list-disc pl-4 space-y-2 text-muted-foreground">
                        <li>Client achète un seul type de produit</li>
                        <li>Client paie en espèces</li>
                        <li>Vous avez beaucoup de clients qui attendent</li>
                        <li>Vous voulez faire une vente simple et rapide</li>
                      </ul>
                      <p className="font-medium mt-4">Exemple :</p>
                      <div className="bg-muted p-4 rounded-lg">
                        <p>Un client entre dans votre boutique et dit :</p>
                        <p className="text-primary-600 mt-2">"Je veux 5 poulets de race X"</p>
                        <div className="mt-2">
                          <p className="font-medium">→ Utilisez la Vente Rapide :</p>
                          <ol className="list-decimal pl-4 mt-1 space-y-1">
                            <li>Trouvez le produit "Poulet race X"</li>
                            <li>Entrez la quantité "5"</li>
                            <li>Cliquez sur "Vente Rapide"</li>
                            <li>C'est fait ! La vente est enregistrée</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Mode "Panier"</h3>
                    <div className="pl-4 space-y-2">
                      <p className="font-medium">Quand l'utiliser :</p>
                      <ul className="list-disc pl-4 space-y-2 text-muted-foreground">
                        <li>Client achète plusieurs types de produits différents</li>
                        <li>Client veut payer par un autre moyen que les espèces</li>
                        <li>Client demande une facture détaillée</li>
                        <li>Vous devez faire une vente à crédit</li>
                      </ul>
                      <p className="font-medium mt-4">Exemple :</p>
                      <div className="bg-muted p-4 rounded-lg">
                        <p>Un client entre dans votre boutique et dit :</p>
                        <p className="text-primary-600 mt-2">"Je veux 3 poulets de race X, 2 de race Y, et je paierai par mobile money"</p>
                        <div className="mt-2">
                          <p className="font-medium">→ Utilisez le Panier :</p>
                          <ol className="list-decimal pl-4 mt-1 space-y-1">
                            <li>Pour chaque type de poulet :
                              <ul className="list-disc pl-4 mt-1">
                                <li>Trouvez le produit</li>
                                <li>Entrez la quantité</li>
                                <li>Cliquez sur "Panier"</li>
                              </ul>
                            </li>
                            <li>Ouvrez le panier</li>
                            <li>Choisissez "Mobile Money" comme moyen de paiement</li>
                            <li>Validez la vente</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm text-muted-foreground">
                      💡 Conseil : En cas de doute, utilisez le mode "Panier". Il est plus complet et permet de tout faire.
                      La "Vente Rapide" est juste un raccourci pour les ventes simples et fréquentes.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>

        <div className="fixed top-6 right-6 z-50">
          <Button onClick={() => setIsCartDialogOpen(true)} size="lg" className="bg-primary-600 hover:bg-primary-700 text-white shadow-lg transition-all duration-200 transform hover:scale-105 my-[46px]">
            <ShoppingCart className="mr-2 h-5 w-5" />
            Voir le panier
            {cartItems.length > 0 && <span className="ml-2 bg-white text-primary-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">
                {cartItems.length}
              </span>}
          </Button>
        </div>

        <div className="relative max-w-xl mx-auto mb-8">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Rechercher un produit..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 w-full border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200" />
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {currentItems?.map(item => {
          const parsedData = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
          return <Card key={item.id} className="p-6 bg-white shadow-md hover:shadow-lg transition-all duration-200 border border-gray-100 py-[14px] px-[4px]">
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {parsedData?.Race || parsedData?.name || 'Sans nom'}
                    </h3>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 border border-input bg-background hover:bg-accent hover:text-accent-foreground touch-action-manipulation" onClick={e => {
                      e.preventDefault();
                      const content = document.querySelector('[role="tooltip"]');
                      if (content) {
                        content.classList.add('force-show');
                        setTimeout(() => {
                          content.classList.remove('force-show');
                        }, 2000);
                      }
                    }}>
                          <Info className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black text-white p-2 rounded max-w-xs text-center text-sm data-[state=closed]:opacity-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 transition-all" side="top">
                        Sans quantité spécifiée, la vente rapide vendra 1 unité. Pour vendre plus, indiquez la quantité souhaitée.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="flex justify-between">
                      <span>Prix:</span>
                      <span className="font-medium text-gray-900">{(parsedData?.price || 0).toLocaleString()} {currency}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Stock:</span>
                      <span className="font-medium">{item.quantity || 0}</span>
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Input type="number" min="1" max={item.quantity} value={quantities[item.id] || ""} onChange={e => handleQuantityChange(item.id, parseInt(e.target.value) || 0)} placeholder="Qté" className="w-full" />
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white" disabled={!item.quantity || item.quantity <= 0} onClick={() => handleQuickSale(item, quantities[item.id] || 1)}>
                        Vente Rapide
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Input type="number" min="1" max={item.quantity} value={quantities[item.id] || ""} onChange={e => handleQuantityChange(item.id, parseInt(e.target.value) || 0)} placeholder="Qté" className="w-full" />
                      <Button className="w-full" onClick={() => handleAddToCart(item)} disabled={!item.quantity || item.quantity <= 0}>
                        Panier
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>;
        })}
        </div>

        {totalPages > 1 && <Pagination className="justify-center mt-8">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious onClick={handlePreviousPage} className="hover:bg-gray-100 transition-colors duration-200" />
              </PaginationItem>
              <PaginationItem>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {currentPage} sur {totalPages}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext onClick={handleNextPage} className="hover:bg-gray-100 transition-colors duration-200" />
              </PaginationItem>
            </PaginationContent>
          </Pagination>}

        <CartDialog open={isCartDialogOpen} onOpenChange={setIsCartDialogOpen} refetch={refetch} />
      </div>
    </DashboardLayout>;
}
